import { mapText } from "./map-i18n.js";
import { buildPointSampleRows } from "./map-hover-facts.js";
import { characterClassPresentation } from "./map-character-class-presentation.js";
import { isTradeNpcLayerId } from "./map-trade-npc-contract.js";
import {
  resolveTradeNpcBadgeImageUrl,
  resolveTradeNpcImageUrl,
  tradeNpcForKey,
} from "./map-trade-summary.js";

const DEFAULT_LANDMARK_ICON = "information-circle";
const CHARACTER_TARGET_KEY = "character";
const LIVE_EVENTS_LAYER_ID = "live_events";
const TRADE_NPC_TARGET_KEY = "trade_npc";
const HOTSPOT_TARGET_KEY = "hotspot";
const REGION_NODES_LAYER_ID = "region_nodes";
const REGION_NODE_TARGET_KEY = "region_node";

const LANDMARK_HOVER_TARGET_PRESENTATION = Object.freeze({
  [CHARACTER_TARGET_KEY]: {
    icon: "wifi-fill",
    label: "Character",
  },
  bookmark: {
    icon: "bookmark",
    labelKey: "info.status.bookmark",
    swatchRgb: "239 92 31",
  },
  [TRADE_NPC_TARGET_KEY]: {
    icon: "trade-origin",
    label: "NPC",
    swatchRgb: "255 196 66",
  },
  [HOTSPOT_TARGET_KEY]: {
    icon: "map-pin",
    label: "Hotspot",
    swatchRgb: "255 179 56",
  },
  [REGION_NODE_TARGET_KEY]: {
    icon: "map-pin",
    label: "Node",
    swatchRgb: "244 240 232",
  },
  waypoint: {
    icon: "map-pin",
    labelKey: "info.status.waypoint",
    swatchRgb: "244 240 232",
  },
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function coordinateKey(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(3) : "";
}

function targetWorldX(target) {
  return target?.worldX ?? target?.world_x;
}

function targetWorldZ(target) {
  return target?.worldZ ?? target?.world_z;
}

function landmarkHoverPresentation(layerId, targetKey) {
  const normalizedTargetKey = trimString(targetKey);
  if (
    normalizedTargetKey === REGION_NODE_TARGET_KEY &&
    layerId !== REGION_NODES_LAYER_ID
  ) {
    return null;
  }
  return LANDMARK_HOVER_TARGET_PRESENTATION[normalizedTargetKey] || null;
}

function resolveLandmarkMarkerImageUrl(marker) {
  const imagePath = trimString(marker?.imagePath);
  if (!imagePath) {
    return "";
  }
  const resolver = globalThis.window?.__fishystuffResolveCdnUrl;
  return typeof resolver === "function" ? trimString(resolver(imagePath)) : "";
}

function detailFactValueFromSample(sample, key) {
  for (const section of Array.isArray(sample?.detailSections) ? sample.detailSections : []) {
    for (const fact of Array.isArray(section?.facts) ? section.facts : []) {
      if (trimString(fact?.key) === key) {
        return trimString(fact?.value);
      }
    }
  }
  return "";
}

function tradeNpcKeyFromSample(sample) {
  const npcKey = Number.parseInt(detailFactValueFromSample(sample, "npc_key"), 10);
  return Number.isInteger(npcKey) && npcKey > 0 ? npcKey : null;
}

function characterCatalogEntryFromSample(sample, characterCatalog) {
  if (trimString(sample?.layerId) !== LIVE_EVENTS_LAYER_ID) {
    return null;
  }
  const identityKey = trimString(sample?.characterIdentityKey);
  if (!identityKey) {
    return null;
  }
  const characters = Array.isArray(characterCatalog?.characters)
    ? characterCatalog.characters
    : [];
  return characters.find((entry) => trimString(entry?.identityKey) === identityKey) || null;
}

function characterPresentationFromSample(sample, characterCatalog) {
  const entry = characterCatalogEntryFromSample(sample, characterCatalog);
  if (!entry || !Object.hasOwn(entry, "characterClassKey")) {
    return null;
  }
  return characterClassPresentation(entry.characterClassKey);
}

function characterPresentationFields(presentation) {
  if (!presentation) {
    return {
      characterPortraitState: "unknown",
    };
  }
  return {
    characterPortraitState: "known",
    characterClassKey: presentation.characterKey,
    characterClassName: presentation.name,
    characterPortraitUrl: presentation.portraitUrl,
  };
}

function buildTargetLandmarkRows(hover, tradeNpcCatalog, characterCatalog) {
  const layerSamples = Array.isArray(hover?.layerSamples) ? hover.layerSamples : [];
  const landmarkMarkerImageUrl = resolveLandmarkMarkerImageUrl(hover?.landmarkMarker);
  const seen = new Set();
  let winningNodeMarkerConsumed = false;
  return layerSamples.flatMap((sample) => {
    const layerId = trimString(sample?.layerId);
    const tradeNpc = isTradeNpcLayerId(layerId)
      ? tradeNpcForKey(tradeNpcCatalog, tradeNpcKeyFromSample(sample))
      : null;
    const targets = Array.isArray(sample?.targets) ? sample.targets : [];
    return targets.flatMap((target, targetIndex) => {
      if (!isPlainObject(target)) {
        return [];
      }
      const targetKey = trimString(target.key);
      const presentation = landmarkHoverPresentation(layerId, targetKey);
      const value = trimString(target.label);
      if (!presentation || !value) {
        return [];
      }
      const worldX = targetWorldX(target);
      const worldZ = targetWorldZ(target);
      const rowKey = [
        "landmark-hover",
        layerId,
        targetKey,
        coordinateKey(worldX),
        coordinateKey(worldZ),
        value,
      ].join(":");
      if (seen.has(rowKey)) {
        return [];
      }
      seen.add(rowKey);
      const usesWinningNodeMarker =
        !winningNodeMarkerConsumed &&
        layerId === REGION_NODES_LAYER_ID &&
        targetKey === REGION_NODE_TARGET_KEY &&
        Boolean(landmarkMarkerImageUrl);
      const markerImageUrl = usesWinningNodeMarker ? landmarkMarkerImageUrl : "";
      winningNodeMarkerConsumed ||= usesWinningNodeMarker;
      const characterPresentation =
        targetKey === CHARACTER_TARGET_KEY
          ? characterPresentationFromSample(sample, characterCatalog)
          : null;
      const isLiveEventCharacter =
        layerId === LIVE_EVENTS_LAYER_ID && targetKey === CHARACTER_TARGET_KEY;
      return [
        {
          kind: "landmark-hover",
          key: rowKey || `landmark-hover:${targetIndex}`,
          layerId,
          targetKey,
          label: isLiveEventCharacter
            ? ""
            : presentation.label || mapText(presentation.labelKey),
          value,
          ...(isLiveEventCharacter
            ? {}
            : { icon: presentation.icon || DEFAULT_LANDMARK_ICON }),
          ...(isLiveEventCharacter
            ? characterPresentationFields(characterPresentation)
            : {}),
          ...(markerImageUrl ? { markerImageUrl } : {}),
          ...(tradeNpc
            ? {
                npcKey: tradeNpc.npcKey,
                imageUrl: resolveTradeNpcImageUrl(tradeNpc),
                badgeImageUrl: resolveTradeNpcBadgeImageUrl(tradeNpc),
              }
            : {}),
          ...(presentation.swatchRgb ? { swatchRgb: presentation.swatchRgb } : {}),
          worldX,
          worldZ,
        },
      ];
    });
  });
}

export function patchTouchesLandmarkHoverSignals(patch) {
  if (!isPlainObject(patch)) {
    return false;
  }
  return Boolean(
    patch._map_runtime?.catalog?.fish != null ||
      patch._map_character_catalog != null ||
      patch._map_ui?.layers?.sampleHoverVisibleByLayer != null,
  );
}

export function buildLandmarkHoverRows({
  hover = null,
  characterCatalog = null,
  stateBundle = null,
  pointSamplesEnabled = true,
  tradeNpcCatalog = null,
  zoneCatalog = [],
} = {}) {
  const pointRows =
    pointSamplesEnabled === false
      ? []
      : buildPointSampleRows({ source: hover, stateBundle, zoneCatalog });
  return [
    ...pointRows,
    ...buildTargetLandmarkRows(hover, tradeNpcCatalog, characterCatalog),
  ];
}
